import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useContext } from 'react';
import { doc, runTransaction } from 'firebase/firestore';
import { db } from '../../../../firebase';
import { PatientDataContext } from '../../../../context/PatientDataContext';
import { useAuth } from '../../../../hooks/useAuth';
import useWorkflowConfig from '../../../../hooks/useWorkflowConfig';
import { AutoResizeTextarea } from '../../../../components/AutoResizeTextarea';
import {
  getActionableOtherConditions,
  getFlatOtherConditionsStepTitle,
} from '../../../../utils/diagnosticDecisionRouting';

// Hierarchical orgs distinguish between secondary, comorbid, and both.
// Flat-model orgs simplify to a single "Confirm diagnosis" option since
// the primary/secondary/comorbid hierarchy is removed — all confirmed
// conditions are treated as co-occurring without ranking.
const HIERARCHICAL_DECISIONS = [
  'Confirm as Secondary',
  'Confirm as Comorbid',
  'Confirm as Secondary and Comorbid',
  'Unable to confirm',
  'Rule out',
];

const FLAT_DECISIONS = [
  'Confirm diagnosis',
  'Unable to confirm',
  'Rule out',
];

// TOC workflow uses a simplified two-option model: the clinician either
// provides follow-up next steps for a condition or rules it out entirely.
// "Confirm" and "Unable to confirm" are not applicable here because the TOC
// assessment is an ADHD-focused pathway — other conditions are noted for
// onward referral rather than formally diagnosed.
const TOC_DECISIONS = [
  'Provide next steps',
  'Rule out',
];

export default function ConfirmProvisionalSecondaryDiagnoses({ setFormValid, canEdit = true, onDirty }) {
  const { patientId } = useParams();
  const { patientDataModel, updatePatientFirestore } = useContext(PatientDataContext);
  const { claims } = useAuth();
  const wf = useWorkflowConfig(patientDataModel, claims);

  const decisions = wf.isTOCWorkflow ? TOC_DECISIONS : (wf.isFlatDifferentials ? FLAT_DECISIONS : HIERARCHICAL_DECISIONS);

  // The list of conditions to present for diagnostic decisions.
  //
  // Hierarchical orgs: the clinician explicitly selected secondary/comorbid
  // conditions during the CI DF — stored in secondaryComorbidDiagnoses.
  //
  // Flat-model orgs: there is no secondary/comorbid distinction.  The
  // clinician selected "conditions in consideration" (differentialDiagnoses)
  // during the SSI DF.  ADHD is excluded here because it already has its
  // own dedicated diagnostic decision page (DD_ADHD / 1_DD.js).
  // Sorted alphabetically to match the backend's sorted iteration order
  // in llm_processor_dd.py, ensuring condition N in the UI maps to the
  // same dd_secondaryCondition_N_sentence generated by the backend.
  const conditionsToDecide = useMemo(() => {
    return [...getActionableOtherConditions({
      patientDataModel,
      isFlatDifferentials: wf.isFlatDifferentials,
    })].sort();
  }, [patientDataModel, wf.isFlatDifferentials]);
  
  // State for tracking each diagnosis decision and notes
  const [diagnosisDecisions, setDiagnosisDecisions] = useState({});

  // Ref that always holds the latest diagnosisDecisions — used by
  // handleNotesBlur to avoid reading a stale render closure when blur
  // fires between the last keystroke and the next React re-render.
  const diagnosisDecisionsRef = useRef(diagnosisDecisions);
  useEffect(() => { diagnosisDecisionsRef.current = diagnosisDecisions; }, [diagnosisDecisions]);

// Function to check if all diagnoses have decisions - wrapped in useCallback
const updateFormValidity = useCallback((decisions) => {
  if (!setFormValid) return;
  
  // Form is valid if every diagnosis has a decision
  const isValid = conditionsToDecide.length > 0 && 
    conditionsToDecide.every(diagnosis => 
      decisions[diagnosis]?.decision && 
      decisions[diagnosis]?.decision !== null
    );
  
  setFormValid(isValid);
}, [conditionsToDecide, setFormValid]);

  // One-shot hydration from Firestore — only runs on initial mount or when
  // the condition list content genuinely changes (e.g. clinician goes back
  // to DF and adds/removes a condition). Does NOT re-run on patientDataModel
  // refreshes triggered by writes to other fields (which would clobber
  // in-progress typed-but-unblurred notes). Matches the isInitialLoad pattern
  // used in PAD pages (1_REV.js, 2_DIS.js, 6_PAD_TOC.js).
  const isInitialLoad = useRef(true);
  const prevConditionsRef = useRef(null);

  useEffect(() => {
    const conditionsKey = conditionsToDecide.join(',');
    if (!isInitialLoad.current && prevConditionsRef.current === conditionsKey) return;
    isInitialLoad.current = false;
    prevConditionsRef.current = conditionsKey;

    const stored = patientDataModel?.dataModel?.['4_diagnosticDecision']
      ?.sections?.[2]?.finalComorbidDiagnoses || {};
    const initialDecisions = {};
    conditionsToDecide.forEach(diagnosis => {
      initialDecisions[diagnosis] = stored[diagnosis] || {
        decision: null,
        notes: ''
      };
    });

    setDiagnosisDecisions(initialDecisions);

    if (conditionsToDecide.length > 0 && setFormValid) {
      updateFormValidity(initialDecisions);
    }
  }, [patientDataModel, conditionsToDecide, setFormValid, updateFormValidity]);

  // Clean up stale finalComorbidDiagnoses entries when the DF has changed.
  //
  // When a clinician removes a condition from the Differential Formulation,
  // its entry persists in finalComorbidDiagnoses because the normal
  // updatePatientFirestore path (deepMerge) never deletes keys. This
  // direct Firestore transaction follows the established read-delete-write
  // pattern used in RD_*.js files for approvedParagraphs cleanup. It runs
  // once on mount before user interaction, so the updatePatientFirestore
  // queue is empty for this section and there's no conflict.
  useEffect(() => {
    if (!canEdit || conditionsToDecide.length === 0) return;

    let isMounted = true;
    const cleanupStaleEntries = async () => {
      try {
        await runTransaction(db, async (transaction) => {
          const docRef = doc(db, 'patients', patientId);
          const docSnap = await transaction.get(docRef);
          if (!docSnap.exists()) return;

          const data = docSnap.data();
          const existing = data.dataModel?.['4_diagnosticDecision']
            ?.sections?.['2']?.finalComorbidDiagnoses || {};

          const staleKeys = Object.keys(existing).filter(
            k => !conditionsToDecide.includes(k)
          );
          if (staleKeys.length === 0) return;

          const cleaned = { ...existing };
          staleKeys.forEach(k => delete cleaned[k]);

          if (isMounted) {
            transaction.update(docRef, {
              'dataModel.4_diagnosticDecision.sections.2.finalComorbidDiagnoses': cleaned
            });
          }
        });
      } catch (error) {
        console.error('Failed to clean up stale finalComorbidDiagnoses:', error);
      }
    };

    cleanupStaleEntries();
    return () => { isMounted = false; };
  }, [canEdit, conditionsToDecide, patientId]);

  // Handle updating a diagnosis decision
  const handleUpdateDiagnosis = (diagnosis, decision) => {
    if (!canEdit) return;
    const updatedDecisions = {
      ...diagnosisDecisions,
      [diagnosis]: {
        ...diagnosisDecisions[diagnosis],
        decision
      }
    };
    
    setDiagnosisDecisions(updatedDecisions);
    updateFormValidity(updatedDecisions);
    onDirty?.();
    
    // Send only the changed condition to Firestore. The deepMerge in
    // PatientDataContext layers this onto fresh Firestore data inside a
    // transaction, so other conditions are preserved. Spreading the entire
    // finalComorbidDiagnoses from the stale React context would cause a
    // race condition when two conditions are changed in quick succession
    // (the second spread reverts the first change).
    updatePatientFirestore(patientId, {
      '4_diagnosticDecision': {
        sections: {
          '2': {
            finalComorbidDiagnoses: {
              [diagnosis]: {
                ...updatedDecisions[diagnosis]
              }
            }
          }
        }
      }
    });
  };
  
  // Handle notes text changes — updates local state AND enqueues a
  // debounced Firestore write on every keystroke. This ensures that if
  // the user switches conditions (unmounting the textarea) before blur
  // fires, the typed text is already queued for persistence. The 200ms
  // debounce in PatientDataContext naturally coalesces rapid keystrokes.
  const handleNotesChange = (diagnosis, notes) => {
    if (!canEdit) return;
    const updated = {
      ...diagnosisDecisions,
      [diagnosis]: {
        ...diagnosisDecisions[diagnosis],
        notes
      }
    };
    setDiagnosisDecisions(updated);
    onDirty?.();

    updatePatientFirestore(patientId, {
      '4_diagnosticDecision': {
        sections: {
          '2': {
            finalComorbidDiagnoses: {
              [diagnosis]: { ...updated[diagnosis] }
            }
          }
        }
      }
    });
  };
  
  // Save notes to Firestore when user finishes typing.
  // Reads from diagnosisDecisionsRef.current so we always persist the
  // latest keystroke even if blur fires before React re-renders with the
  // newest state (stale-closure guard). Same partial-update pattern as
  // handleUpdateDiagnosis — only the changed condition is included to
  // avoid the spread-merge race.
  const handleNotesBlur = (diagnosis) => {
    if (!canEdit) return;
    onDirty?.();
    const latest = diagnosisDecisionsRef.current[diagnosis];
    if (!latest) return;
    updatePatientFirestore(patientId, {
      '4_diagnosticDecision': {
        sections: {
          '2': {
            finalComorbidDiagnoses: {
              [diagnosis]: { ...latest }
            }
          }
        }
      }
    });
  };
  
  // Get placeholder text based on decision type
const getPlaceholderText = (decision) => {
  switch(decision) {
    case 'Confirm as Secondary':
    case 'Confirm as Comorbid':
    case 'Confirm as Secondary and Comorbid':
    case 'Confirm diagnosis':
      return "Please specify details of diagnosis including subtype if applicable and recommended next steps";
    case 'Unable to confirm':
      return "Please specify rationale and recommended next steps";
    case 'Provide next steps':
      return "Please specify recommended next steps for this condition (e.g. onward referral, further assessment)";
    case 'Rule out':
      return "Please specify rationale and recommended next steps, if applicable";
    default:
      return "Please provide additional notes";
  }
};

  const sectionTitle = wf.isFlatDifferentials
    ? getFlatOtherConditionsStepTitle(wf.isTOCWorkflow)
    : 'Confirm Secondary and/or Comorbid Diagnoses';

  const emptyStateMessage = wf.isFlatDifferentials
    ? "No other conditions were identified during the assessment."
    : "No provisional secondary or comorbid diagnoses were identified during the clinical interview.";

  if (!conditionsToDecide || conditionsToDecide.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6 w-full">
        <h2 className="text-xl font-bold mb-6">{sectionTitle}</h2>
        <div className="p-4 bg-gray-50 rounded-lg">
          <p className="text-gray-700">{emptyStateMessage}</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="bg-gray-50 rounded-2xl border border-gray-200 text-left p-6 shadow-md mb-6">
    <h2 className="text-left text-xl font-bold mb-6">{sectionTitle}</h2>

    {conditionsToDecide.map((diagnosis, index) => {
      const diagnosisData = diagnosisDecisions[diagnosis] || { decision: null, notes: '' };

      return (
        <div key={index} className="mb-6 pb-6 border-b border-gray-200 last:border-0">
          {/* Secondary or Comorbid Diagnosis Confirmation */}
          <div className="mb-4 flex items-start">
            <div className="flex items-center gap-1 w-96">
              <h4 className="text-left mt-2 font-semibold text-gray-700">            
                  {diagnosis || "No diagnosis recorded"}            
              </h4>
            </div>
            
            <div className="ml-5">
              <div className="w-96 mt-2">
                <ul className="text-left space-y-2">
                  {decisions.map((decision) => (
                    <li
                      key={decision}
                      onClick={() => canEdit && handleUpdateDiagnosis(diagnosis, decision)}
                      className={`
                        flex flex-col gap-3 p-2 
                        rounded-lg transition-all duration-300 
                        ${!canEdit
                          ? 'cursor-not-allowed opacity-75'
                          : 'cursor-pointer group'
                        }
                        ${diagnosisData.decision === decision
                          ? 'bg-violet-300 outline outline-2 outline-violet-500 font-semibold'
                          : canEdit
                            ? 'bg-violet-100 hover:bg-violet-200'
                            : 'bg-violet-100'
                        }
                      `}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`
                          flex-shrink-0 w-4 h-4 rounded-full border-2
                          transition-all duration-200 ease-in-out
                          ${diagnosisData.decision === decision
                            ? 'border-violet-700 bg-violet-700'
                            : canEdit
                              ? 'border-gray-300 group-hover:border-violet-400'
                              : 'border-gray-300'
                          }
                          flex items-center justify-center
                        `}>
                          {diagnosisData.decision === decision && (
                            <div className="w-2 h-2 bg-white rounded transition-all duration-200 ease-in-out" />
                          )}
                        </div>
                        <span className={`font-semibold transition-colors duration-200 ${diagnosisData.decision === decision ? 'text-violet-900' : 'text-gray-700'}`}>
                          {decision}
                        </span>
                      </div>
                      
                      {diagnosisData.decision === decision && (
                        <AutoResizeTextarea
                          placeholder={getPlaceholderText(decision)}
                          disabled={!canEdit}
                          className={`font-medium w-full mt-1 p-2 border rounded-md text-black min-h-[60px] resize-none overflow-hidden ${
                            canEdit ? 'bg-white' : 'bg-gray-100 cursor-not-allowed'
                          }`}
                          value={diagnosisData.notes}
                          onChange={(e) => canEdit && handleNotesChange(diagnosis, e.target.value)}
                          onBlur={() => canEdit && handleNotesBlur(diagnosis)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      );
    })}
  </div>
);
}